import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, Output, ViewChild } from '@angular/core';
import { Status } from '@df/core/status';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { PwaIntegrationService } from '@df/module-pwa/pwa-integration.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ResultComponent } from 'projects/app-default/src/app/ui/resut/result.component';
import { BehaviorSubject } from 'rxjs';
import { LocalCustomerModel } from '../../../../customer/customer.model.decorator';
import { UserImageRole, UserImagesService } from '../../../../customer/user-images.service';
import { PwaNativeBridgeService } from '../../../../module/pwa/pwa-native-bridge.service';

@Component({
    selector: 'profile-image',
    templateUrl: './profile-image.html',
    styleUrls: ['./profile-image.scss'],
    standalone: true,
    imports: [DfImageDirective, TranslationModule, ResultComponent, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileImageComponent extends UiComponent {
    @ViewChild('fileInput')
    fileInput!: ElementRef<HTMLInputElement>;

    /**
     * The image role to use for saving the image
     */
    @Input()
    imageRole: UserImageRole = UserImageRole.Profile;

    /**
     * Index for indexed images (e.g., family members, horses)
     */
    @Input()
    index?: number;

    @Input({ required: true })
    title!: string;

    @Input({ required: true })
    uploadTitle!: string;

    @Input()
    titleFont = 'label';

    @Output()
    onFileChange = new BehaviorSubject<File | undefined>(undefined);

    protected customerModel = inject(LocalCustomerModel);
    protected userImagesService = inject(UserImagesService);
    protected pwaIntegrationService = inject(PwaIntegrationService);
    protected pwaNativeBridgeService = inject(PwaNativeBridgeService);
    protected imageStatus = new Status();
    protected base64: string | undefined;

    override ngOnInit(): void {
        this.subscriptions.add(
            this.userImagesService.userImages.subscribe(() => {
                this.detectChanges();
            })
        );
    }

    /**
     * Get the current image URL for display
     */
    get currentImageUrl(): string | undefined {
        return this.customerModel.getUserImage(this.imageRole, this.index);
    }

    /**
     * Check if there's an uploaded image (either from file or existing)
     */
    get hasImage(): boolean {
        return !!(this.base64 || this.customerModel.getUserImage(this.imageRole, this.index));
    }

    /**
     * Handle click on the upload area
     */
    protected async onImageClick(): Promise<void> {
        this.fileInput?.nativeElement.click();
    }

    /**
     * Handle file selection
     */
    protected async onFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        await this.processSelectedFile(file);
    }

    /**
     * Process the selected file (from either file input or native bridge)
     */
    private async processSelectedFile(file?: File): Promise<void> {
        if (!file) {
            this.imageStatus.busy = false;
            this.detectChanges();
            return;
        }

        this.imageStatus.busy = true;
        this.detectChanges();

        if (!this.isValidImageType(file)) {
            // Validate file type - only PNG, JPEG, JPG, HEIF, and HEIC are allowed
            this.imageStatus.error = 'Only PNG, JPEG, JPG, HEIF, and HEIC files are allowed';
        } else if (file.size > 5 * 1024 * 1024) {
            // Validate file size (max 5MB)
            this.imageStatus.error = 'Image size must be less than 5MB';
        } else {
            try {
                // Normalize orientation by rendering through canvas (bakes rotation into pixels)
                const normalizedFile = await this.userImagesService.normalizeImage(file);
                this.base64 = await this.fileToBase64(normalizedFile);
                this.onFileChange.next(normalizedFile);
            } catch (error) {
                console.error('Error processing image orientation:', error);
                // Fallback to original file if processing fails
                this.base64 = await this.fileToBase64(file);
                this.onFileChange.next(file);
            }
        }
        this.imageStatus.busy = false;
        this.detectChanges();
    }

    /**
     * Validate if the file type is allowed (PNG, JPEG, JPG, HEIF, and HEIC)
     */
    private isValidImageType(file: File): boolean {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/heif', 'image/heic'];
        const allowedExtensions = ['.png', '.jpg', '.jpeg', '.heif', '.heic'];

        // Check MIME type
        const isValidMimeType = allowedTypes.includes(file.type.toLowerCase());

        // Check file extension as fallback
        const fileName = file.name.toLowerCase();
        const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

        return isValidMimeType || isValidExtension;
    }

    /**
     * Convert file to base64 string for preview
     */
    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }
}
